<script setup lang="ts">
/**
 * Aviso de NÍVEL das gamificações: "sua loja/roleta/check-in/promocode abre no
 * Bronze 1". É o antigo `VipStoreLevelNotice` generalizado — a loja continua
 * sendo quem o usa inteiro (no lugar da grade), e os modais da roletinha, do
 * Check-in Diário e do promocode o usam `flat`, dentro do BaseNoticeSheet, no
 * lugar da lista de requisitos de KYC que existia lá.
 *
 * A régua NÃO mora aqui: o degrau mínimo de cada gamificação é editável no
 * backoffice do core (CRM › Elegibilidade) e VEM no /players/me
 * (`gamifications.<slug>.min_level`), junto com o nível atual (`level`). Este
 * componente só recebe os dois slugs e escreve a frase — por isso sobe sozinho
 * num harness, sem Pinia.
 *
 * As cores e o emblema vêm de `LEVEL_TIERS` (o elo que o degrau mínimo nomeia)
 * em vez de hex solto: é a mesma tabela que pinta a trilha de níveis, então o
 * Bronze do aviso e o Bronze da vitrine não podem divergir.
 */
import { crmLevelTier } from '@/utils/levels';
import { crmLevelLabel } from '@/utils/apostatudo/crm-level-slug';
import { getAssetsUrlForCDN, localeLink } from '@/utils/template';
import { useLoyaltyHubStore } from '@/stores/loyalty-hub';

const props = withDefaults(
  defineProps<{
    // Sujeito da frase, já com o possessivo: 'Sua loja', 'Sua roleta', 'Seu
    // check-in', 'Seu promocode'.
    subject: string;
    // Slug CRM do degrau mínimo ('BRONZE LV 1'), como veio no payload. `null` =
    // o core não mandou o nome — a frase fica genérica, sem inventar degrau.
    minLevel: string | null;
    // Slug CRM do nível atual do jogador (`level` do /players/me). `null` = sem
    // registro no CRM ainda ("você ainda não tem nível").
    currentLevel?: string | null;
    // `true` = dentro do BaseNoticeSheet: sem caixa, sem título (a frase do
    // sheet já é o título) — só emblema, texto e CTA.
    flat?: boolean;
  }>(),
  { currentLevel: null, flat: false },
);

// Quem embute em modal fecha o modal quando o CTA navega.
const emit = defineEmits<{ (e: 'navigate'): void }>();

const minLabel = computed(() => crmLevelLabel(props.minLevel));
const currentLabel = computed(() => crmLevelLabel(props.currentLevel));

// Elo do degrau mínimo, pra emblema e cores (mesmo helper que o BaseNoticeSheet
// usa pra tingir a folha — os dois têm que concordar).
const tier = computed(() => crmLevelTier(props.minLevel));

// Fallbacks com os valores do Bronze: se um dia a chave mudar, o aviso continua
// desenhado (só perde o vínculo) em vez de renderizar sem cor.
const badge = computed(() =>
  getAssetsUrlForCDN(
    tier.value?.badge ?? '/assets/images/levels/badges/bronze.webp',
  ),
);

const color = computed(() => tier.value?.color ?? '#FF6A00');
const accent = computed(() => tier.value?.accent ?? '#FFC324');

const router = useRouter();
const hub = useLoyaltyHubStore();

// Com o Clube Fidelidade aberto, a rota por baixo é de outro dono — o CTA abre
// a `LevelsSheet` dentro do hub em vez de navegar. Fora dele, vai pra
// /vip/levels. Vale igual pra loja embutida e pros modais abertos pelo hub.
function seeLevels() {
  if (hub.isOpen) {
    hub.openLevels();
  } else {
    router.push(localeLink('/vip/levels'));
  }

  emit('navigate');
}
</script>

<template>
  <section
    class="lgn"
    :class="{ 'lgn--flat': props.flat }"
    role="note"
    :style="{ '--lgn-color': color, '--lgn-accent': accent }"
  >
    <img
      alt=""
      aria-hidden="true"
      class="lgn__badge"
      decoding="async"
      :src="badge"
    />

    <!-- "Sua loja abre no nível Bronze 1": o trecho do nível na cor do elo,
         como no corpo. -->
    <h2
      v-if="!props.flat"
      class="lgn__title"
    >
      <template v-if="minLabel">
        {{ props.subject }} abre no <strong>nível {{ minLabel }}</strong>
      </template>
      <template v-else>{{ props.subject }} exige um nível mínimo</template>
    </h2>

    <p class="lgn__text">
      <template v-if="minLabel">
        A partir do <strong>{{ minLabel }}</strong> esta função fica disponível
        para você.
      </template>
      <template v-else>
        Esta função exige um nível mínimo na trilha de níveis.
      </template>
      <template v-if="currentLabel">
        Você está no <strong>{{ currentLabel }}</strong>
        <template v-if="minLabel">
          — alcance o <strong>{{ minLabel }}</strong> para liberar.
        </template>
        <template v-else> — suba de nível para liberar. </template>
      </template>
      <template v-else>
        Você ainda não tem nível
        <template v-if="minLabel">
          — alcance o <strong>{{ minLabel }}</strong> para liberar.
        </template>
        <template v-else> — suba de nível para liberar. </template>
      </template>
    </p>

    <button
      class="lgn__cta"
      type="button"
      @click="seeLevels"
    >
      Ver meus níveis
    </button>
  </section>
</template>

<style scoped lang="scss">
// Animação por @keyframes, nunca `transition`: o reset global do main.scss mata
// `transition` no app inteiro.
@keyframes lgn-in {
  from {
    opacity: 0;
    transform: translateY(8px);
  }

  to {
    opacity: 1;
    transform: none;
  }
}

.lgn {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 28px 20px 24px;
  text-align: center;
  // Aviso é só leitura: sem seleção/arraste de texto, nem do emblema.
  user-select: none;
  -webkit-user-select: none;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  // Fallback antes do gradiente: se o navegador não conhecer `color-mix`, ele
  // descarta a declaração INTEIRA e o painel ficaria transparente.
  background: #010612;
  // Brilho do elo saindo de cima, sobre o fundo escuro.
  background:
    radial-gradient(
      120% 80% at 50% 0%,
      color-mix(in srgb, var(--lgn-color) 18%, transparent) 0%,
      transparent 70%
    ),
    #010612;
  animation: lgn-in 260ms ease-out both;
}

// Dentro do BaseNoticeSheet: o painel já é a caixa e já tem partículas no
// fundo, então aqui sobra só o conteúdo. `position: relative` + z pra ficar
// acima da camada de partículas (z-0) do sheet.
.lgn--flat {
  position: relative;
  z-index: 1;
  gap: 8px;
  padding: 0;
  border: 0;
  border-radius: 0;
  background: none;
  animation: none;
}

.lgn__badge {
  width: 84px;
  height: auto;
  -webkit-user-drag: none;
}

.lgn--flat .lgn__badge {
  width: 64px;
}

.lgn__title {
  margin: 0;
  color: #fff;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.3;

  strong {
    color: var(--lgn-accent);
  }
}

.lgn__text {
  max-width: 420px;
  margin: 0;
  color: rgba(255, 255, 255, 0.65);
  font-size: 13px;
  font-weight: 500;
  line-height: 1.5;

  strong {
    color: var(--lgn-accent);
    font-weight: 700;
  }
}

.lgn__cta {
  margin-top: 6px;
  padding: 10px 22px;
  border: 0;
  border-radius: 10px;
  background: linear-gradient(
    92deg,
    var(--lgn-color) 0%,
    var(--lgn-accent) 100%
  );
  color: #1a0f00;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.2;
  cursor: pointer;
}

@media (min-width: 768px) {
  .lgn:not(.lgn--flat) {
    padding: 40px 24px 36px;

    .lgn__badge {
      width: 104px;
    }

    .lgn__title {
      font-size: 22px;
    }

    .lgn__text {
      font-size: 14px;
    }
  }
}
</style>
